import React, { useState, useCallback, useRef, memo } from 'react';
import { Box, Skeleton } from '@mui/material';
import { v3Theme } from '../theme';

/**
 * V3 Item Icon Component - Universal icon component for all item icons in V3
 * Encapsulates all styling, caching, and loading logic
 * 
 * @param {string} icon - Icon name without path/extension (e.g., "steel", "gold")
 * @param {number} size - Size in pixels (width and height will be the same)
 * @param {function} onClick - Optional click handler
 * @param {boolean} disabled - Optional disabled state (reduces opacity)
 */
const ItemIconV3 = memo(({ icon, size = 32, onClick, disabled = false }) => {
  const [extension, setExtension] = useState('webp');
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);
  const checkTimerRef = useRef(null);

  // Reset state when icon changes
  React.useEffect(() => {
    if (!icon) return;
    
    setExtension('webp');
    setHasFailed(false);
    setIsLoading(true);
    
    // Cleanup timer on unmount or icon change
    return () => {
      if (checkTimerRef.current) {
        clearTimeout(checkTimerRef.current);
        checkTimerRef.current = null;
      }
    };
  }, [icon]);

  // Memoized error handler
  const handleError = useCallback(() => {
    if (extension === 'webp') {
      setExtension('png');
    } else {
      setHasFailed(true);
      setIsLoading(false);
    }
  }, [extension]);

  // Memoized load handler
  const handleLoad = useCallback(() => {
    setIsLoading(false);
    if (checkTimerRef.current) {
      clearTimeout(checkTimerRef.current);
      checkTimerRef.current = null;
    }
  }, []);

  // Memoized click handler
  const handleClick = useCallback(() => {
    if (onClick && !disabled) {
      onClick();
    }
  }, [onClick, disabled]);

  // Memoized ref callback for Safari fix
  const imgRefCallback = useCallback((imgElement) => {
    if (!imgElement) return;
    
    // Clear any existing timer
    if (checkTimerRef.current) {
      clearTimeout(checkTimerRef.current);
    }
    
    // Check if already loaded (cached images)
    if (imgElement.complete && imgElement.naturalHeight > 0) {
      setIsLoading(false);
    } else {
      // Safari fallback - ensure loading state updates
      checkTimerRef.current = setTimeout(() => {
        if (imgElement?.complete && imgElement?.naturalHeight > 0) {
          setIsLoading(false);
        }
      }, 100);
    }
  }, []);

  // Container styles
  const containerStyles = {
    width: size,
    height: size,
    borderRadius: v3Theme.ui.itemIconRadius || '8px',
    overflow: 'hidden',
    position: 'relative',
    flexShrink: 0,
    cursor: onClick && !disabled ? 'pointer' : 'default',
    opacity: disabled ? 0.5 : 1,
    backgroundColor: isLoading || hasFailed ? v3Theme.secondary : 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  };

  // Handle missing or failed icons
  if (!icon || hasFailed) {
    return (
      <Box sx={containerStyles} onClick={handleClick}>
        <Box
          component="img"
          src="/img/items/unknown.png"
          alt="Unknown item"
          loading="lazy"
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.3,
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={containerStyles} onClick={handleClick}>
      {/* Skeleton loader */}
      {isLoading && (
        <Skeleton 
          variant="rectangular" 
          width={size - 4} 
          height={size - 4}
          animation="wave"
          sx={{
            position: 'absolute',
            borderRadius: v3Theme.ui.itemIconRadius || '8px',
            bgcolor: 'rgba(255, 255, 255, 0.15)',
          }}
        />
      )}
      
      {/* The actual image */}
      <Box
        component="img"
        src={`/img/items/${icon}.${extension}`}
        alt={`${icon} icon`}
        loading="lazy" // Native lazy loading
        decoding="async" // Non-blocking decode
        onError={handleError}
        onLoad={handleLoad}
        ref={imgRefCallback}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          opacity: isLoading ? 0 : 1,
          transition: 'opacity 0.2s ease-in-out',
          // Performance optimizations
          transform: 'translateZ(0)', // Force GPU layer
          backfaceVisibility: 'hidden',
        }}
      />
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom equality check - only re-render if these props change
  return (
    prevProps.icon === nextProps.icon &&
    prevProps.size === nextProps.size &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.onClick === nextProps.onClick
  );
});

ItemIconV3.displayName = 'ItemIconV3';

export default ItemIconV3;